/**
 * useRoster — Fetches and manages roster assignment data.
 *
 * Provides the full roster (all battalions), SOD assignments,
 * pending change requests, and member list for assignment pickers.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';

// ── Types ─────────────────────────────────────────────────────────────

export interface RosterAssignment {
  id: number;
  battalion_id: number;
  unit_id: string;
  seat_id: string;
  member_id: number;
  display_name: string;
  email: string;
  assigned_at: string;
}

export interface SODAssignment {
  id: number;
  member_id: number;
  role_label: string;
  display_name: string;
  email: string;
  assigned_at: string;
}

export interface ChangeRequest {
  id: number;
  battalion_id: number;
  unit_id: string;
  seat_id: string;
  proposed_member_id: number | null;
  proposed_member_name: string | null;
  current_member_id: number | null;
  current_member_name: string | null;
  requested_by: number;
  requester_name: string;
  status: string;
  reviewed_by: number | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface RosterMember {
  id: number;
  display_name: string;
  email: string;
  battalion_id: number | null;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useRoster() {
  const [assignments, setAssignments] = useState<RosterAssignment[]>([]);
  const [sodAssignments, setSODAssignments] = useState<SODAssignment[]>([]);
  const [pendingChanges, setPendingChanges] = useState<ChangeRequest[]>([]);
  const [members, setMembers] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoster = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [rosterRes, sodRes] = await Promise.all([
        apiFetch<{ assignments: RosterAssignment[] }>('/api/roster'),
        apiFetch<{ assignments: SODAssignment[] }>('/api/roster/sod'),
      ]);

      setAssignments(rosterRes.assignments);
      setSODAssignments(sodRes.assignments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingChanges = useCallback(async () => {
    try {
      const res = await apiFetch<{ changes: ChangeRequest[] }>('/api/roster/changes?status=pending');
      setPendingChanges(res.changes);
    } catch {
      // Non-critical — officers may not have access
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiFetch<{ members: RosterMember[] }>('/api/roster/members');
      setMembers(res.members);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  // ── Mutations ─────────────────────────────────────────────────────

  const proposeChange = useCallback(
    async (data: {
      battalion_id: number;
      unit_id: string;
      seat_id: string;
      proposed_member_id: number | null;
      notes?: string;
    }) => {
      await apiFetch('/api/roster/propose', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await Promise.all([fetchRoster(), fetchPendingChanges()]);
    },
    [fetchRoster, fetchPendingChanges],
  );

  const approveChange = useCallback(
    async (changeId: number) => {
      await apiFetch(`/api/roster/changes/${changeId}/approve`, { method: 'PUT' });
      await Promise.all([fetchRoster(), fetchPendingChanges()]);
    },
    [fetchRoster, fetchPendingChanges],
  );

  const rejectChange = useCallback(
    async (changeId: number, notes?: string) => {
      await apiFetch(`/api/roster/changes/${changeId}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ notes }),
      });
      await fetchPendingChanges();
    },
    [fetchPendingChanges],
  );

  // ── Helpers ───────────────────────────────────────────────────────

  /** Look up who is assigned to a specific seat */
  const getAssignment = useCallback(
    (battalionId: number, unitId: string, seatId: string): RosterAssignment | undefined => {
      return assignments.find((a) => a.battalion_id === battalionId && a.unit_id === unitId && a.seat_id === seatId);
    },
    [assignments],
  );

  /** Check if a seat has a pending change */
  const getPendingChange = useCallback(
    (battalionId: number, unitId: string, seatId: string): ChangeRequest | undefined => {
      return pendingChanges.find((c) => c.battalion_id === battalionId && c.unit_id === unitId && c.seat_id === seatId);
    },
    [pendingChanges],
  );

  return {
    assignments,
    sodAssignments,
    pendingChanges,
    members,
    loading,
    error,
    fetchRoster,
    fetchPendingChanges,
    fetchMembers,
    proposeChange,
    approveChange,
    rejectChange,
    getAssignment,
    getPendingChange,
  };
}
